/**
 * What the notification bell keeps between screens, and when it forgets it.
 *
 * #218: the Manager and Admin bells read the same unread count as the Player's, plus the unread
 * notifications themselves, each its own link (managerNotificationLink). A Player's subscription
 * request opens the Manager's Players page on its Pending tab.
 *
 * #196. The Player bell shows two kinds of thing:
 *   - Notifications (announcements). How many are unread, and the newest one, come from the API
 *     (Announcement/GetUnreadNotifications), so nothing about them is stored here. Opening the
 *     Notifications page marks them read on the server, and announceNotificationsSeen() tells a
 *     bell that is already on screen to drop its count.
 *   - Auction start reminders (the ReceiveAuctionNotification hub message). The server does not
 *     store these, so a bell that remounts on every screen change can only remember them here.
 *   - #227: system notifications - what the Player still has to do (fill in the profile, buy a
 *     subscription, pay a failed renewal). The API works them out on every read of the bell
 *     (systemItems) and stores nothing, so nothing is kept here either; announceBellChanged() asks
 *     a bell on screen to read them again once the Player has done one.
 *
 * This replaces three loose localStorage keys TopNavbar used to write: "notiMess" (the last
 * notification, the only one ever shown), "messageCount" (counted, never displayed) and
 * "auctionCreate" (the first reminder's sentence, which decided its own link by looking for the
 * words "secondary market"). No sign-out removed all three, so the next account to sign in on the
 * same browser was shown the previous one's reminder. clearNotificationBell() now runs on every
 * sign-in and sign-out.
 *
 * This module imports nothing from the app but lotClock.js, which imports nothing itself, because
 * helper.js and apiClient.js both call clearNotificationBell() and must not import each other
 * through it.
 */

import { parseServerUtc } from './lotClock';

/** Dispatched on window when the Notifications page has marked everything read. */
export const NOTIFICATIONS_SEEN_EVENT = 'xcutta:notifications-seen';

/** Dispatched on window when something the bell's system notifications depend on has changed (#227). */
export const BELL_CHANGED_EVENT = 'xcutta:bell-changed';

const AUCTION_REMINDERS_KEY = 'auctionReminders';

/** The keys the bell used before #196; still sitting in returning users' browsers. */
const LEGACY_BELL_KEYS = ['notiMess', 'messageCount', 'auctionCreate'];

/**
 * Turns a hub row or a stored row into the shape kept here, or null when it is not usable.
 *
 * @param {object} row A ReceiveAuctionNotification row ({auctionId, auctionName, startDate,
 *   message, ...}) or a row this module stored ({auctionId, auctionName, startsAt, message}).
 * @returns {{auctionId: number, auctionName: string, startsAt: number, message: string}|null}
 */
const toReminder = (row) => {
    const auctionId = Number(row?.auctionId);
    const startsAt = typeof row?.startsAt === 'number' ? row.startsAt : parseServerUtc(row?.startDate);
    if (!Number.isInteger(auctionId) || auctionId <= 0 || !Number.isFinite(startsAt)) return null;

    return {
        auctionId,
        auctionName: typeof row.auctionName === 'string' ? row.auctionName : '',
        startsAt,
        message: typeof row.message === 'string' ? row.message : '',
    };
};

const byStart = (a, b) => a.startsAt - b.startsAt || a.auctionId - b.auctionId;

const writeReminders = (reminders) => {
    if (reminders.length === 0) {
        localStorage.removeItem(AUCTION_REMINDERS_KEY);
    } else {
        localStorage.setItem(AUCTION_REMINDERS_KEY, JSON.stringify(reminders));
    }
};

/**
 * The start reminders still worth showing, soonest first.
 *
 * A reminder goes once its auction has started: what it announced is then on the auction board
 * itself, and its "starting in 15 minutes" would only be wrong. Anything unreadable is dropped
 * rather than shown.
 *
 * @param {number} [now] The current time, for tests.
 * @returns {Array<{auctionId: number, auctionName: string, startsAt: number, message: string}>}
 */
export const readAuctionReminders = (now = Date.now()) => {
    const raw = localStorage.getItem(AUCTION_REMINDERS_KEY);
    if (!raw) return [];

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        parsed = null;
    }

    const stored = Array.isArray(parsed) ? parsed : [];
    const upcoming = stored.map(toReminder).filter((r) => r && r.startsAt > now);

    if (upcoming.length !== stored.length || !Array.isArray(parsed)) {
        writeReminders(upcoming);
    }

    return upcoming.sort(byStart);
};

/**
 * Adds the rows of a ReceiveAuctionNotification message.
 *
 * Every row is kept, not only the first: one message carries every reminder due for this account
 * at once. A newer reminder for the same auction replaces the older one.
 *
 * @param {Array<object>} rows The message's rows.
 * @param {number} [now] The current time, for tests.
 * @returns {Array<object>} The reminders now kept, as readAuctionReminders returns them.
 */
export const saveAuctionReminders = (rows, now = Date.now()) => {
    const current = readAuctionReminders(now);
    if (!Array.isArray(rows)) return current;

    const byAuction = new Map(current.map((r) => [r.auctionId, r]));
    rows
        .map(toReminder)
        .filter((r) => r && r.startsAt > now)
        .forEach((r) => byAuction.set(r.auctionId, r));

    const next = [...byAuction.values()].sort(byStart);
    writeReminders(next);
    return next;
};

/**
 * Forgets one auction's reminder, once the player has followed it.
 *
 * @param {number} auctionId The auction.
 * @param {number} [now] The current time, for tests.
 * @returns {Array<object>} The reminders left.
 */
export const dismissAuctionReminder = (auctionId, now = Date.now()) => {
    const next = readAuctionReminders(now).filter((r) => r.auctionId !== Number(auctionId));
    writeReminders(next);
    return next;
};

/** Removes the keys the bell used before #196. Run once on load. */
export const purgeLegacyNotificationKeys = () => {
    LEGACY_BELL_KEYS.forEach((key) => localStorage.removeItem(key));
};

/** Forgets everything the bell holds. Every sign-in and sign-out calls it. */
export const clearNotificationBell = () => {
    localStorage.removeItem(AUCTION_REMINDERS_KEY);
    purgeLegacyNotificationKeys();
};

/** Tells a bell on screen that the Notifications page has marked everything read. */
export const announceNotificationsSeen = () => {
    window.dispatchEvent(new Event(NOTIFICATIONS_SEEN_EVENT));
};

/**
 * Runs a handler whenever announceNotificationsSeen() is called.
 *
 * @param {() => void} handler What to do.
 * @returns {() => void} Stops listening.
 */
export const onNotificationsSeen = (handler) => {
    window.addEventListener(NOTIFICATIONS_SEEN_EVENT, handler);
    return () => window.removeEventListener(NOTIFICATIONS_SEEN_EVENT, handler);
};

/**
 * Tells a bell on screen to read its system notifications again (#227): the profile was saved or a
 * promo code redeemed, so "Fill in your personal data" or "Buy Subscription" may be over.
 */
export const announceBellChanged = () => {
    window.dispatchEvent(new Event(BELL_CHANGED_EVENT));
};

/**
 * Runs a handler whenever announceBellChanged() is called.
 *
 * @param {() => void} handler What to do.
 * @returns {() => void} Stops listening.
 */
export const onBellChanged = (handler) => {
    window.addEventListener(BELL_CHANGED_EVENT, handler);
    return () => window.removeEventListener(BELL_CHANGED_EVENT, handler);
};

/**
 * The text of the bell's badge, which is sized for two characters.
 *
 * @param {number} count How many items the bell holds.
 * @returns {string} The count, or "9+" above nine.
 */
export const formatBadgeCount = (count) => {
    const n = Math.max(0, Math.trunc(Number(count) || 0));
    return n > 9 ? '9+' : String(n);
};

/** A Player's subscription request to a Manager (#218, the notification's notificationType). */
export const SUBSCRIPTION_REQUEST_TYPE = 'SubscriptionRequest';

/** How many unread notifications the Manager and Admin bells list; the API sends at most this many. */
export const BELL_LIST_LIMIT = 10;

/**
 * A Player's system notifications (#227, the API's systemItems[].notificationType): what they still
 * have to do, worked out by the server from their account on every read and never stored, so
 * opening the Notifications page does not clear them - doing the thing does.
 */
export const SYSTEM_NOTIFICATION_TYPE = {
    COMPLETE_PROFILE: 'CompleteProfile',
    BUY_SUBSCRIPTION: 'BuySubscription',
    COMPLETE_PAYMENT: 'CompletePayment',
};

/**
 * Where a system notification on the Player bell leads (#227).
 *
 * @param {{notificationType?: string|null}} item A row of the API's systemItems.
 * @returns {string} The profile for CompleteProfile; Account Settings, where subscriptions are bought
 *   and a failed renewal is paid, for anything else.
 */
export const systemNotificationLink = (item) =>
    item?.notificationType === SYSTEM_NOTIFICATION_TYPE.COMPLETE_PROFILE
        ? '/Player/Profile'
        : '/Player/Account_settings';

/**
 * Where a notification on the Manager or Admin bell, or on their Notifications page, leads (#218).
 *
 * @param {{notificationType?: string|null}} notification A notification from the API or the hub.
 * @returns {{to: string, state?: {targetTab: string}}} A subscription request opens Players on its
 *   Pending tab (UserSuper.js reads `targetTab`); anything else opens the Notifications page.
 */
export const managerNotificationLink = (notification) =>
    notification?.notificationType === SUBSCRIPTION_REQUEST_TYPE
        ? { to: '/SuperAdmin/UserSuper', state: { targetTab: 'Pending' } }
        : { to: '/SuperAdmin/SuperNotification' };

/**
 * Puts a notification received live at the top of the bell's list: newest first, each id once,
 * at most BELL_LIST_LIMIT, as the API lists them.
 *
 * @param {object[]} items What the bell lists now.
 * @param {{id: number}} notification The ReceiveNotification payload.
 * @returns {object[]} The new list.
 */
export const prependBellItem = (items, notification) =>
    [notification, ...(items || []).filter((item) => item?.id !== notification?.id)].slice(0, BELL_LIST_LIMIT);

/**
 * The bell after a ReceiveNotification push: one more unread, the notification as the newest
 * message and at the top of the list (#218 review).
 *
 * Unless the list already holds it. The bell's read (Announcement/GetUnreadNotifications) runs on
 * every screen change, and a read that ran after the notification was written counted it already;
 * the push that follows must not count it again. The read lists the newest notifications, so one
 * it counted and its push can still race with is among them. A push without an id is always new.
 *
 * @param {{unreadCount: number, latestMessage: string|null, items: object[]}} bell The bell now.
 * @param {{id?: number, message: string}} notification The ReceiveNotification payload.
 * @returns {{unreadCount: number, latestMessage: string|null, items: object[]}} The bell after it.
 */
export const receiveBellNotification = (bell, notification) => {
    const listed = notification?.id != null && (bell.items || []).some((item) => item?.id === notification.id);
    if (listed) return bell;

    return {
        unreadCount: bell.unreadCount + 1,
        latestMessage: notification.message,
        items: prependBellItem(bell.items, notification),
    };
};
